import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { Warranty } from '@wsphere/warranties/domain';
import { TenantFeature, TenantFeatureService } from '@wsphere/warranties/web';
import moment from 'moment';
import { shareReplay } from 'rxjs';
import { WarrantyCustomerPortalService } from '../../../services';

const RENEWAL_WINDOW_DAYS = 90;

const RENEWABLE_STATUSES: Warranty.Status[] = [Warranty.Status.ACTIVATED, Warranty.Status.REGISTERED];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-warranty-details-card',
  templateUrl: './warranty-details-card.component.html',
  standalone: false,
})
export class WarrantyDetailsCardComponent {
  @Output() renewed = new EventEmitter<Warranty.RenewalResult>();

  // Check if tenant has access to declaration page feature
  canShowDeclaration$ = this.tenantFeatureSvc.isFeatureEnabled(TenantFeature.DECLARATION_PAGE).pipe(shareReplay(1));

  get isNoLongerActive(): boolean {
    return !!this.warranty && Warranty.isEnded(this.warranty.status);
  }
  private _warranty?: Warranty.Model;

  @Input() set warranty(value: Warranty.Model | undefined) {
    this._warranty = value;
    this.refreshRenewalEligibility();
  }

  get warranty(): Warranty.Model | undefined {
    return this._warranty;
  }

  /**
   * AC 1.4/1.5: whether the API confirms this warranty currently has an eligible published
   * policy to renew against. Locally-obvious ineligibility (status/date/frozen) is checked first
   * so the Renew action never even flashes on screen while that request is in flight, and so a
   * plainly-ineligible warranty never triggers a network call at all.
   */
  renewalEligible = false;

  private renewalEligibilityRequestId = 0;

  private get passesLocalRenewalChecks(): boolean {
    if (!this.warranty || !this.warranty.expiryDate) return false;
    if (!RENEWABLE_STATUSES.includes(this.warranty.status) || this.warranty.frozen) return false;

    const daysUntilExpiry = moment
      .utc(this.warranty.expiryDate)
      .startOf('day')
      .diff(moment.utc().startOf('day'), 'days');
    return daysUntilExpiry >= 0 && daysUntilExpiry <= RENEWAL_WINDOW_DAYS;
  }

  private async refreshRenewalEligibility() {
    this.renewalEligible = false;
    if (!this.warranty || !this.passesLocalRenewalChecks) return;

    const requestId = ++this.renewalEligibilityRequestId;
    try {
      const eligibility = await this.warrantySvc.checkRenewalEligibility(this.warranty.id);
      if (requestId === this.renewalEligibilityRequestId) this.renewalEligible = eligibility.eligible;
    } catch {
      if (requestId === this.renewalEligibilityRequestId) this.renewalEligible = false;
    }
  }

  constructor(
    private tenantFeatureSvc: TenantFeatureService,
    private warrantySvc: WarrantyCustomerPortalService
  ) {}
}
